import React, { forwardRef } from "react";

export type LinkBaseProps = {
  /**
   * Default element is an anchor tag when an `href` prop is provided and a button when one is not.
   * Provide an `as` prop to override this behavior and use a different element (e.g. span, div, Next.js's Link component...)
   */
  as?: React.ElementType;
  className?: string;
  /**
   * LinkBase defaults to a `div` if neither a link nor an onClick handler are provided, and not custom `as` prop has been set.
   * If your component needs a different default (e.g. a `span`), specify a `defaultAs` prop.
   */
  defaultAs?: React.ElementType;
  /**
   * When an `href` prop is provided, LinkBase will render an anchor element unless an `as` prop is set.
   */
  href?: string;
  /**
   * When an `onClick` prop is provided, LinkBase will render a button element unless an `as` or `href` prop is set.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;

  children: React.ReactNode;
};

const getElementType = ({
  as,
  defaultAs,
  href,
  onClick,
}: LinkBaseProps): React.ElementType => {
  if (as) {
    return as;
  }

  if (href) {
    return "a";
  }

  if (onClick) {
    return "button";
  }

  return defaultAs || "div";
};

/**
 * LinkBase is intended for internal use.
 * It tries to determine semantically correct markup to use for a given element based on the provided props.
 */
const LinkBase = forwardRef<HTMLElement, LinkBaseProps>((props, ref) => {
  const { children, href, onClick, ...others } = props;
  const Element = getElementType(props);

  const otherProps = {
    href,
    onClick,
    ...others,
  };

  return (
    <Element {...otherProps} ref={ref}>
      {children}
    </Element>
  );
});

export default LinkBase;
